import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Container,
    colors,
} from '@mui/material';
import { ArrowBack, Check } from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';

// Import images
import citizenMapBlue from '../../assets/imgs/citizen_map_blue.png';
import connectPeople from '../../assets/imgs/connect-people.png';
import informationBg from '../../assets/imgs/informationbg2.png';

// Logo is in public folder
const Leader360logo = '/leader360-logo.png';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();

    const [step, setStep] = useState<'phone' | 'otp' | 'success'>('phone');
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [sessionToken, setSessionToken] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'error',
    });
    const [userData, setUserData] = useState<any>(null);

    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Mask phone number for display
    const getMaskedPhone = () => {
        if (phone.length >= 10) {
            return `+91 ${phone.slice(0, 2)}xxx${phone.slice(-2)}`;
        }
        return phone;
    };

    const handleSendOtp = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        if (phone.length < 5) {
            setSnackbar({ open: true, message: 'Please enter a valid phone number or email', severity: 'error' });
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.post('/login', { phone });
            if (response?.data?.status === 'error') {
                throw new Error(response.data.message);
            }
            const { session_token } = response.data.data;
            setSessionToken(session_token);
            setStep('otp');
        } catch (err: any) {
            const message = err.response?.data?.message || err.message || 'Failed to send OTP';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) {
            value = value.slice(-1);
        }
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto focus next input
        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
        // Prevent form submission when pressing Enter in OTP fields
        if (e.key === 'Enter') {
            e.preventDefault();
            const otpString = otp.join('');
            if (otpString.length === 6) {
                // Manually trigger the form submit handler
                const form = (e.target as HTMLElement).closest('form');
                if (form) {
                    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }
            }
        }
    };

    const handleVerifyOtp = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            setSnackbar({ open: true, message: 'Please enter complete 6-digit OTP', severity: 'error' });
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.post('/verify-otp', {
                session_token: sessionToken,
                otp: otpString,
            });
            if (response.data?.status === 'error') {
                throw new Error(response.data.message);
            }
            if (response.data.status === 'success') {

                const { token, user } = response.data.data;
                setUserData({ token, user });
                setStep('success');
            }
        } catch (err: any) {
            console.log(err);
            const message = err.response?.data?.message || err.message || 'Invalid OTP';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleContinue = () => {
        if (userData) {
            login(userData.token, userData.user);
            const { user } = userData;
            if (user.role === 'super_admin') {
                navigate('/super-admin');
            } else if (user.role === 'admin') {
                navigate('/admin');
            } else if (user.role === 'sub_admin') {
                navigate('/admin');
            }
        }
    };
    useEffect(() => {
        if (userData) {
            setTimeout(() => {
                handleContinue();
            }, 2000);
        }
    }, [userData]);
    const handleResendOtp = async () => {
        setIsLoading(true);
        try {
            const response = await api.post('/login', { phone });
            if (response?.data?.status === 'error') {
                throw new Error(response.data.message);
            }
            const { session_token } = response.data.data;
            setSessionToken(session_token);
            setOtp(['', '', '', '', '', '']);
        } catch (err: any) {
            const message = err.response?.data?.message || err.message || 'Failed to resend OTP';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Common styles
    const primaryBlue = '#1a3a5c';
    const accentGold = '#e9a825';
    const accentGreen = '#4caf50';
    const textGray = '#6b7c93';

    // Render footer
    const renderFooter = () => (
        <Typography
            variant="body2"
            sx={{
                textAlign: 'center',
                mt: 3,
                color: textGray,
                fontSize: '0.85rem',
            }}
        >
            Secured Login Powered by{' '}
            <Typography
                component="span"
                sx={{
                    color: accentGold,
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                OTP Verification
            </Typography>
        </Typography>
    );

    // ========== VERIFICATION SUCCESS PAGE ==========
    if (step === 'success') {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    pt: 6,
                    backgroundImage: `url(${informationBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: '#f5f7fa',
                }}
            >
                {/* Logo */}
                <Box sx={{ mb: 4 }}>
                    <Box
                        component="img"
                        src={Leader360logo}
                        alt="Leader360"
                        sx={{ height: 60, width: 'auto' }}
                    />
                </Box>

                {/* Success Card */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 5,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
                        textAlign: 'center',
                        maxWidth: 400,
                        width: '90%',
                    }}
                >
                    {/* Green Checkmark Circle */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            border: `3px solid ${accentGreen}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3,
                        }}
                    >
                        <Check sx={{ fontSize: 40, color: accentGreen }} />
                    </Box>

                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: primaryBlue,
                            mb: 1,
                        }}
                    >
                        Verification Success
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: textGray,
                            mb: 4,
                            lineHeight: 1.6,
                        }}
                    >
                        Congratulations! You have been Successfully
                        <br />
                        Authenticated
                    </Typography>

                    {/* Continue Button */}
                    <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        onClick={handleContinue}
                        sx={{
                            py: 1.5,
                            borderRadius: 3,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${accentGreen} 0%, #43a047 100%)`,
                            color: '#fff',
                            boxShadow: '0 4px 14px rgba(76, 175, 80, 0.35)',
                            '&:hover': {
                                background: `linear-gradient(135deg, #43a047 0%, #388e3c 100%)`,
                            },
                        }}
                    >
                        Continue
                    </Button>

                    {renderFooter()}
                </Paper>
            </Box>
        );
    }

    // ========== SIGN IN & OTP VERIFY PAGES ==========
    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                position: 'relative',
                overflow: 'hidden',
                backgroundImage: `url(${informationBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: '#f5f7fa',
            }}
        >
            <Container sx={{
                display: 'flex',
                position: 'relative',
                overflow: 'hidden',
            }}>

                {/* Left Side - Form */}
                <Box
                    sx={{
                        flex: { xs: 1, md: '0 0 50%' },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        // alignItems: 'center',
                        p: { xs: 3, sm: 4, md: 5 },
                        overflowY: 'auto',
                        height: '100vh',
                    }}
                >

                    {/* Form Container */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}
                    >
                        {step === 'phone' ? (
                            // ========== SIGN IN STEP ==========
                            <Box
                                sx={{
                                    width: '100%',
                                }}
                            >

                                {/* Logo */}
                                <Box sx={{ mb: 4, width: '100%', textAlign: 'center' }}>
                                    <Box
                                        component="img"
                                        src={Leader360logo}
                                        alt="Leader360"
                                        sx={{ height: { xs: 50, md: 68 } }}
                                    />
                                </Box>


                                <Card>
                                    <CardContent sx={{ py: 4, px: 5 }}>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 700,
                                                color: primaryBlue,
                                                mb: 0.5,
                                                fontSize: { xs: '1.75rem', sm: '2rem' },
                                            }}
                                        >
                                            Sign In
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: textGray,
                                                mb: 4,
                                                fontSize: '0.9rem',
                                            }}
                                        >
                                            Enter your phone number or email to sign in !
                                        </Typography>

                                        <form onSubmit={handleSendOtp}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: primaryBlue,
                                                    fontWeight: 600,
                                                    mb: 1,
                                                    fontSize: '0.85rem',
                                                }}
                                            >
                                                Phone Number or Email*
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                placeholder="Enter your Phone Number or Email ID"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                sx={{
                                                    mb: 3,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: '#fff',
                                                        borderRadius: 2.5,
                                                        '& fieldset': { borderColor: '#e0e4e8' },
                                                        '&:hover fieldset': { borderColor: '#c5ccd3' },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: accentGold,
                                                            borderWidth: 2,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: primaryBlue,
                                                        fontSize: '0.9rem',
                                                        py: 1.5,
                                                        '&::placeholder': {
                                                            color: '#9ca8b5',
                                                            opacity: 1,
                                                        },
                                                    },
                                                }}
                                            />
                                            <Button
                                                fullWidth
                                                size="large"
                                                variant="contained"
                                                type="submit"
                                                disabled={isLoading || phone.length < 5}
                                                sx={{
                                                    py: 1.4,
                                                    borderRadius: 2.5,
                                                    fontSize: '0.95rem',
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    background: `linear-gradient(135deg, ${accentGold} 0%, #d4941f 100%)`,
                                                    color: '#fff',
                                                    boxShadow: '0 4px 14px rgba(233, 168, 37, 0.35)',
                                                    '&:hover': {
                                                        background: `linear-gradient(135deg, #d4941f 0%, #c4850f 100%)`,
                                                    },
                                                    '&:disabled': {
                                                        background: '#e2e8f0',
                                                        color: '#94a3b8',
                                                    },
                                                }}
                                            >
                                                {isLoading ? (
                                                    <CircularProgress size={22} sx={{ color: primaryBlue }} />
                                                ) : (
                                                    'Send OTP'
                                                )}
                                            </Button>
                                            {renderFooter()}
                                        </form>
                                    </CardContent>
                                </Card>
                            </Box>
                        ) : (
                            // ========== OTP VERIFY STEP ==========
                            <Box>

                                {/* Logo */}
                                <Box sx={{ mb: 4, width: '100%', textAlign: 'center' }}>
                                    <Box
                                        component="img"
                                        src={Leader360logo}
                                        alt="Leader360"
                                        sx={{ height: { xs: 50, md: 68 } }}
                                    />
                                </Box>

                                <Card>
                                    <CardContent sx={{ py: 4, px: 5 }}>

                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 700,
                                                color: 'primaryBlue',
                                                mb: 0.5,
                                                fontSize: { xs: '1.75rem', sm: '2rem' },
                                            }}
                                        >
                                            OTP Verify
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: textGray,
                                                mb: 2,
                                                fontSize: '0.9rem',
                                            }}
                                        >
                                            Enter the Verification Code
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: primaryBlue,
                                                mb: 3,
                                                fontSize: '0.85rem',
                                            }}
                                        >
                                            6 digit Code Sent to {getMaskedPhone()}
                                        </Typography>

                                        <Box>
                                            {/* 6-Digit OTP Input Boxes */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    mb: 3,
                                                }}
                                            >
                                                {otp.map((digit, index) => (
                                                    <TextField
                                                        key={index}
                                                        inputRef={(el) => (otpRefs.current[index] = el)}
                                                        value={digit}
                                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                        inputProps={{
                                                            maxLength: 1,
                                                            style: {
                                                                textAlign: 'center',
                                                                fontSize: '1.25rem',
                                                                fontWeight: 600,
                                                                padding: '12px 0',
                                                            },
                                                        }}
                                                        sx={{
                                                            width: 48,
                                                            '& .MuiOutlinedInput-root': {
                                                                bgcolor: '#fff',
                                                                borderRadius: 2,
                                                                '& fieldset': { borderColor: '#e0e4e8' },
                                                                '&:hover fieldset': { borderColor: '#c5ccd3' },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: accentGold,
                                                                    borderWidth: 2,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: primaryBlue,
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Box>

                                            <Button
                                                fullWidth
                                                size="large"
                                                variant="contained"
                                                type="button"
                                                onClick={handleVerifyOtp}
                                                disabled={isLoading || otp.join('').length !== 6}
                                                sx={{
                                                    py: 1.4,
                                                    borderRadius: 2.5,
                                                    fontSize: '0.95rem',
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    background: `linear-gradient(135deg, ${accentGold} 0%, #d4941f 100%)`,
                                                    color: '#ffffff',
                                                    boxShadow: '0 4px 14px rgba(233, 168, 37, 0.35)',
                                                    '&:hover': {
                                                        background: `linear-gradient(135deg, #d4941f 0%, #c4850f 100%)`,
                                                    },
                                                    '&:disabled': {
                                                        background: '#e2e8f0',
                                                        color: '#94a3b8',
                                                    },
                                                }}
                                            >
                                                {isLoading ? (
                                                    <CircularProgress size={22} sx={{ color: primaryBlue }} />
                                                ) : (
                                                    'Verify OTP'
                                                )}
                                            </Button>

                                            {/* Change Phone & Resend OTP */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mt: 2,
                                                }}
                                            >
                                                <Button
                                                    variant="text"
                                                    startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                                                    onClick={() => {
                                                        setStep('phone');
                                                        setOtp(['', '', '', '', '', '']);
                                                    }}
                                                    sx={{
                                                        color: primaryBlue,
                                                        textTransform: 'none',
                                                        fontWeight: 500,
                                                        fontSize: '0.8rem',
                                                        p: 0,
                                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                                                    }}
                                                >
                                                    Change Phone Number or Email
                                                </Button>
                                                <Button
                                                    variant="text"
                                                    onClick={handleResendOtp}
                                                    disabled={isLoading}
                                                    sx={{
                                                        color: accentGold,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                        p: 0,
                                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                                                    }}
                                                >
                                                    Resend OTP?
                                                </Button>
                                            </Box>

                                            {renderFooter()}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Right Side - Image & Content */}
                <Box
                    sx={{
                        flex: { xs: 0, md: '0 0 50%' },
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 3, sm: 4, md: 5 },
                        height: '100vh',
                    }}
                >
                    {step === 'phone' ? (
                        // Sign In - Citizen Map Image
                        <>
                            <Box
                                component="img"
                                src={citizenMapBlue}
                                alt="Citizens"
                                sx={{
                                    objectFit: 'contain',
                                    mb: 3,
                                }}
                            />
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: primaryBlue,
                                    mb: 1,
                                    fontSize: { md: '1.75rem', lg: '2rem' },
                                }}
                            >
                                Leader Connect
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: textGray,
                                    fontWeight: 400,
                                    fontSize: { md: '0.95rem', lg: '1rem' },
                                }}
                            >
                                Empowering the leaders, Simpifing Administration
                            </Typography>

                        </>
                    ) : (
                        // OTP Verify - Connect People Image
                        <>
                            <Box
                                component="img"
                                src={connectPeople}
                                alt="Connect People"
                                sx={{
                                    maxWidth: '85%',
                                    height: 'auto',
                                    maxHeight: '45vh',
                                    objectFit: 'contain',
                                    mb: 3,
                                }}
                            />
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: primaryBlue,
                                    mb: 1,
                                    fontSize: { md: '1.75rem', lg: '2rem' },
                                }}
                            >
                                Connect . Engage . Transform
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: textGray,
                                    fontWeight: 400,
                                    fontSize: { md: '0.95rem', lg: '1rem' },
                                }}
                            >
                                Helping Citizens to solve there needs
                            </Typography>
                        </>
                    )}
                </Box>
            </Container>

            {/* Snackbar for error messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Login;
